<script lang="ts">
  import Drawer from "./lib/Drawer.svelte";
  import Icon from "./lib/Icon.svelte";
  import Pad from "./lib/Pad.svelte";
  import tooltip from "./lib/actions/tooltip";
  import copyText from "./lib/actions/copyText";

  import { largeScreenStore, padStore } from "./lib/stores";
  import { onMount } from "svelte";

  const { curRunID, specStatus, enableDebug, workerStatus } = padStore;

  let drawer;
  let innerWidth = 0;

  let pageTitle = "glompad";

  const fmtRelTime = (status_created_at): string => {
    const last_run_dt = new Date(status_created_at);
    const delta = (new Date().valueOf() - last_run_dt.valueOf()) / 1000;
    let ret: string;

    if ($enableDebug) {
      return delta.toFixed().toString() + " seconds ago";
    }

    if (delta < 60) {
      ret = "just now";
    } else if (delta < 120) {
      ret = "about a minute ago";
    } else if (delta < 2700) {
      ret = (delta / 60).toFixed().toString() + " minutes ago";
    } else if (delta < 5400) {
      ret = "about an hour ago";
    } else if (delta < 86400) {
      ret = "about " + (delta / 3600).toFixed().toString() + " hours ago";
    } else {
      ret = "on " + last_run_dt.toLocaleString();
    }
    return ret;
  };

  let last_status_time = null;
  let last_status_time_text = "";

  onMount(() => {
    const interval = setInterval(() => {
      if ($curRunID > 0) {
        last_status_time = $specStatus.created_at;
        last_status_time_text = fmtRelTime(last_status_time);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  let container_class = "";
  $: {
    container_class = $largeScreenStore ? "large-screen" : "small-screen";
    pageTitle = $curRunID ? `[${$curRunID}] glompad` : "glompad";
  }
</script>

<svelte:window bind:innerWidth />

<svelte:head>
  <title>{pageTitle}</title>
</svelte:head>

<a href="#pad-content" class="skip-link">Skip to content</a>

<div id="container" class={container_class}>
  <Drawer bind:this={drawer} />

  <div class="box header">
    <h1 id="title">
      <div
        class="menu-icon-bg"
        on:click|stopPropagation={drawer.toggleOpen}
        on:keydown|stopPropagation={drawer.toggleOpen}
      >
        <Icon name="menu" stroke="currentColor" />
      </div>

      <a href="{import.meta.env.BASE_URL}#"> glompad </a>
    </h1>
    <div id="pad-actions">
      {#if last_status_time}
        <span class="last-run">(last run {last_status_time_text})</span>
      {/if}
      {#if $workerStatus === 'running'}
        <button
          id="run-button"
          class="cancel"
          aria-label="Cancel computation"
          on:click={padStore.interruptGlom}
          use:tooltip={{
            content: "Cancel running computation",
            placement: "bottom",
            delay: [400, 0],
          }}><Icon name="x" /></button
        >
      {:else}
        <button
          id="run-button"
          aria-label="Run glom"
          disabled={$workerStatus === 'loading'}
          on:click={padStore.executeGlom}
          use:tooltip={{
            content: $workerStatus === 'loading' ? 'Loading Python...' : 'Run (or Ctrl-Enter via keyboard)',
            placement: "bottom",
            delay: [400, 0],
          }}><Icon name="play" /></button
        >
      {/if}
      <button
        class="link-button"
        data-name="permalink"
        aria-label="Copy shareable link"
        use:tooltip={{
          content: "Copy shareable link to clipboard",
          placement: "bottom",
          delay: [400, 0],
        }}
        use:copyText={() => window.location.href}
      >
        <Icon name="link" />
      </button>
    </div>
  </div>

  <main id="pad-content">
    <Pad class="box glompad" />
  </main>

  <div class="box footer">
    <!-- svelte-ignore security-anchor-rel-noreferrer -->
    <a href="https://github.com/mahmoud/glom" target="_blank" aria-label="glom on GitHub"
      ><Icon name="github" /></a
    >
    <!-- svelte-ignore security-anchor-rel-noreferrer -->
    <a href="https://glom.readthedocs.io/en/latest/" target="_blank" aria-label="glom documentation"
      ><Icon name="book-open" /></a
    >
  </div>
</div>
{#if $workerStatus === 'loading'}
  <div class="loading-banner" data-testid="python-status">Loading Python environment...</div>
{/if}
{#if $enableDebug}
  <div style:max-width="80vw">
    <h3>Debug</h3>
    <p>Worker: {$workerStatus}</p>
  </div>
{/if}

<style>
  h1 {
    user-select: none;
    font-family: Nunito, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu,
      Cantarell, "Noto Sans", sans-serif;
    letter-spacing: -0.8px;
    font-weight: 700;
  }

  #title * {
    text-decoration: none;
    color: var(--primary-color-8);
  }

  .menu-icon-bg :global(*) {
    margin: 0 auto;
  }

  .menu-icon-bg {
    cursor: pointer;
    border-radius: 50%;
    background: var(--primary-color-2);
    color: var(--primary-color-8);
    width: 38px;
    height: 36px;
    top: 11px;
    display: inline-block;
    vertical-align: middle;
    overflow: hidden;
    text-align: center;
    text-decoration: none;
    transition: color 0.15s ease, background 0.15s ease;
    font-size: 70%;
    padding: 4px 2px;
  }

  .menu-icon-bg:hover {
    background: var(--primary-color-3);
  }

  #container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
    max-height: calc(100vh - 88px);
  }

  .last-run {
    color: var(--gray-4);
  }

  :global(.small-screen) .last-run {
    display: none;
  }

  .box {
    display: flex;
  }

  :global(.glompad) {
    margin: 0 0.5rem;
    padding: 0.5rem;
    background: var(--gray-0);
    flex: 1;
  }

  .header {
    height: 48px;
    padding-left: 10px;
  }

  #pad-actions {
    align-self: right;
    margin: 12px 18px 0 auto;
    vertical-align: baseline;
  }

  #pad-actions button {
    cursor: pointer;
    background: var(--gray-1);
    color: var(--gray-5);
    border: 1px solid var(--gray-5);
    border-radius: 3px;
    height: 34px;
    width: 34px;
  }

  #pad-actions button:hover {
    background: var(--gray-0);
    border-color: var(--primary-color-8);
    color: var(--primary-color-8);
  }

  #pad-actions #run-button {
    width: 100px;
  }

  .footer {
    height: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-top: 10px;
  }

  .footer * {
    margin: 0 5px 0 5px;
  }

  .skip-link {
    position: absolute;
    left: -9999px;
    z-index: 999;
  }

  .skip-link:focus {
    left: 10px;
    top: 10px;
    background: var(--gray-0);
    color: var(--gray-8);
    padding: 8px;
    border-radius: 4px;
    text-decoration: none;
    border: 2px solid var(--primary-color-8);
  }

  main {
    display: flex;
    flex: 1;
    min-height: 0;
  }
</style>
